import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import clsx from 'clsx';

import { Category } from '@srcDS/js/components/templates/journal/journalArticle/JournalArticle';
import { useLayoutContext } from '../../../../provider/layout/LayoutContext';
import mergeClasses from '../../../../utils/shallowMerge';
import BackToTop from '../../../atoms/backToTop';
import { Text } from '../../../typography/text/Text';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import { formatUrl } from '../utils';
import { theme } from './_vars';
import './JournalNavigation.global.scss';
import defaultStyles from './JournalNavigation.module.scss';

interface JournalNavigationProps {
  title?: string;
  description?: string;
  categories?: Category[];
  activeSlug?: string;
  pageTitle?: string;
  hideNavigation?: boolean;
  currentCategory?: Category;
  hideBreadcrumbs?: boolean;
  customClasses?: Record<string, string>;
}

const JournalNavigation = ({
  title,
  description,
  categories,
  activeSlug,
  pageTitle,
  hideNavigation,
  currentCategory,
  hideBreadcrumbs,
  customClasses,
}: JournalNavigationProps) => {
  // @ts-ignore
  const { homePageUrl } = useSelector((state) => state.app.context.project?.data?.cms?.templates);
  const { setAlwaysHideLiveChat } = useLayoutContext();
  const styles = mergeClasses(defaultStyles, customClasses);

  useEffect(() => {
    setAlwaysHideLiveChat(true);
    return () => setAlwaysHideLiveChat(false);
  }, []);

  const breadcrumbs = useMemo(() => {
    const crumbs = [
      {
        id: 'journal-home',
        name: theme === 'sportscraft' ? 'Discover' : 'Journal',
        reference: {
          target: formatUrl(homePageUrl),
          type: 'link',
        },
      },
      {
        id: `article`,
        name: title,
      },
    ];

    if (currentCategory) {
      crumbs.splice(1, 0, {
        id: currentCategory?.slug,
        name: currentCategory?.title,
        reference: {
          target: formatUrl(currentCategory?.url),
          type: 'link',
        },
      });
    }

    return crumbs;
  }, [currentCategory, title]);

  return (
    <div
      className={clsx(styles.container, {
        [styles.withoutCrumbs]: hideBreadcrumbs,
      })}
    >
      {!hideBreadcrumbs && breadcrumbs && <Breadcrumbs className={styles.breadcrumbs} crumbs={breadcrumbs} />}
      <BackToTop />
      <div
        className={clsx(styles.content, styles.customContent, {
          [`${styles.withoutTitle}`]: !pageTitle,
        })}
      >
        {pageTitle && (
          <Link
            className={clsx(styles.titleLink, {
              [styles.disable]: hideBreadcrumbs,
            })}
            to={`/journal`}
          >
            <Text as="h2" className={styles.title} align={theme === 'saba' ? 'center' : 'left'}>
              {pageTitle}
            </Text>
          </Link>
        )}

        {!hideNavigation && (
          <>
            <div className={styles.navigationBlock}>
              <div
                style={{ '--main-categories-count': categories?.length } as React.CSSProperties}
                className={styles.mainCategories}
              >
                {categories &&
                  categories.map((item, index) => (
                    <Link
                      key={`${index}_${item.title}`}
                      to={formatUrl(item.url)}
                      className={clsx(styles.link, {
                        [`${styles.activeLink}`]: item.slug === activeSlug,
                      })}
                    >
                      {item.title}
                    </Link>
                  ))}
              </div>
            </div>
            {description && (
              <div className={styles.descriptionWrapper}>
                <Text
                  as="p"
                  weight={theme === 'saba' ? 'light' : 'normal'}
                  size="sm"
                  align="center"
                  className={styles.description}
                >
                  {description}
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JournalNavigation;
